import * as React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Collapse from "@mui/material/Collapse"; // Import Collapse for transition
import { colors } from "../../constants/colors"; // Adjust this import according to your project
import { dateTimeToView, dateToView, timeToView } from "../../utils/date.utils";
import { icons } from "../../assets/icons/icons";
import { saveAs } from "file-saver";
import { setLoading, unsetLoading } from "../../store/loading/loading.slice";
import { advisorFileApi } from "../../api/advisor-file/advisor-file.api";
import { getFileExtension } from "../../utils/fileTypeDownload";
import { useDispatch } from "react-redux";
import { advisorStudentApi } from "../../api/advisor-student/advisor-student.api";
export interface CardChatProps {
  data: {
    id: number;
    type: string;
    sender: "me" | "you";
    message: string;
    date: string;
    content_type?: string;
    file?: any;
  }[];
  isAll?: boolean;
}
const CardChat = ({ data, isAll }: CardChatProps) => {
  const dispatch = useDispatch();
  const [isContentVisible, setIsContentVisible] = React.useState(true);

  const chatContainerRef = React.useRef<HTMLDivElement | null>(null);

  // Scroll to bottom when data changes (new messages)
  React.useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth", // Smooth scroll to the bottom
      });
    }
  }, [data]); // This will run when the data (chat messages) change

  // Toggle the visibility of card content
  const handleToggleContent = () => {
    setIsContentVisible(!isContentVisible);
  };

  const handleDownloadFile = async (id: number, name_file: string) => {
    dispatch(setLoading());
    await advisorStudentApi
      .downloadFile(id)
      .then((res) => {
        if (res.status < 300) {
          const response = res.data;

          // Get the content type from the server's response headers
          const contentType = res.headers["content-type"];

          // Create a Blob with the content type from the response
          const blob = new Blob([response], { type: contentType });

          // Extract file extension based on content type
          const fileExtension = getFileExtension(contentType || "");

          // Generate a filename with the correct extension (you can also modify this to use a custom name)
          const fileName = `${name_file}.${fileExtension}`;

          // Save the file
          saveAs(blob, fileName);
        }
      })
      .catch((error) => console.log(error))
      .finally(() => dispatch(unsetLoading()));
  };

  return (
    <Card>
      {/* Header Section with color, extra content, and toggle button */}
      <Box
        sx={{
          backgroundColor: colors.themeMainColor,
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 16px",
        }}
      >
        <Typography variant="h6" sx={{ color: "white" }}>
          ประวัติ
        </Typography>

        {/* Extra content with arrow icons */}
        <IconButton onClick={handleToggleContent} sx={{ color: "white" }}>
          {isContentVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Collapse component for animation transition */}
      <Collapse in={isContentVisible} timeout="auto" unmountOnExit>
        <Box
          ref={chatContainerRef} // Attach reference here
          sx={{
            padding: "16px",
            minHeight: isAll && isAll ? "100%" : "500px",
            maxHeight: isAll && isAll ? "100%" : "500px",
            overflowY: "auto",
          }}
        >
          {/* Chat Messages Display */}
          {data &&
            data
              .sort(
                (a: any, b: any) =>
                  new Date(a.date).getTime() - new Date(b.date).getTime()
              ) // Sort by `date`
              .map((chat: any) => (
                <Box
                  key={chat.id}
                  sx={{
                    display: "flex",
                    justifyContent:
                      chat.sender === "me" ? "flex-end" : "flex-start",
                    mb: 2,
                  }}
                >
                  {chat.content_type === "text" ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems:
                          chat.sender === "me" ? "flex-end" : "flex-start",
                        maxWidth: "80%",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "60%",
                          textAlign: chat.sender === "me" ? "right" : "left",
                        }}
                      >
                        {/* Message Format: {type} วันที่ : {date} เวลา : {time} */}
                        <Typography variant="caption">
                          <b>{chat.type}</b> วันที่:{" "}
                          {chat.date ? dateToView(chat.date) : "N/A"} เวลา:{" "}
                          {chat.date ? timeToView(chat.date) : "N/A"}
                        </Typography>
                      </Box>

                      {/* Chat Detail */}
                      <Box
                        sx={{
                          maxWidth: "100%",
                          padding: "8px 12px",
                          borderRadius: "10px",
                          backgroundColor:
                            chat.sender === "me" ? "#F2F4F7" : "#2186E3",
                          color: chat.sender === "me" ? "black" : "white",
                          mt: 0.5, // Margin top to separate message from metadata
                        }}
                      >
                        <Typography variant="body2">{chat.message}</Typography>
                      </Box>
                    </Box>
                  ) : chat.content_type === "file" ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems:
                          chat.sender === "me" ? "flex-end" : "flex-start",
                        maxWidth: "80%",
                        width: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "60%",
                          textAlign: chat.sender === "me" ? "right" : "left",
                        }}
                      >
                        {/* Message Format: {type} วันที่ : {date} เวลา : {time} */}
                        <Typography variant="caption">
                          วันที่: {chat.date ? dateToView(chat.date) : "N/A"}{" "}
                          เวลา: {chat.date ? timeToView(chat.date) : "N/A"}
                        </Typography>
                      </Box>

                      {/* Chat Detail */}

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {chat.sender === "me" ? (
                          <img
                            src={icons.download_chat}
                            alt="download-icon"
                            style={{
                              width: "25px",
                              height: "25px",
                              marginRight: "25px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              handleDownloadFile(
                                chat.file?.id_advisor_student_file,
                                chat.file?.advisor_student_file_name +
                                  "-" +
                                  new Date().getTime()
                              );
                            }}
                          />
                        ) : null}

                        <Box
                          sx={{
                            maxWidth: "100%",
                            padding: "8px 12px",
                            borderRadius: "10px",
                            backgroundColor:
                              chat.sender === "me" ? "#F2F4F7" : "#2186E3",
                            color: chat.sender === "me" ? "black" : "white",
                            mt: 0.5, // Margin top to separate message from metadata
                          }}
                        >
                          {chat.file?.advisor_student_file_type ===
                            "image/png" ||
                          chat.file?.advisor_student_file_type ===
                            "image/jpeg" ? (
                            <a
                              href={`${process.env.REACT_APP_FILE_BASE_API}/${chat.file?.advisor_student_file_path}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={`${process.env.REACT_APP_FILE_BASE_API}/${chat.file?.advisor_student_file_path}`}
                                style={{
                                  maxWidth: "100%", // Ensures responsive rendering
                                  width: "250px",
                                  height: "250px",
                                  objectFit: "cover", // Maintains aspect ratio
                                  borderRadius: "8px", // Optional: Add rounded corners
                                }}
                                alt={
                                  chat.file?.advisor_student_file_name ?? "file"
                                }
                              />
                            </a>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center", // Aligns items in the center vertically
                                gap: "10px", // Adds space between the columns
                              }}
                              onClick={() => {
                                handleDownloadFile(
                                  chat.file?.id_advisor_student_file,
                                  chat.file?.advisor_student_file_name +
                                    "-" +
                                    new Date().getTime()
                                );
                              }}
                            >
                              <div style={{ width: "100px", height: "100px" }}>
                                <img
                                  src={
                                    chat.file?.advisor_student_file_name &&
                                    chat.file?.advisor_student_file_name?.match(
                                      /\.([a-z0-9]+)$/i
                                    )?.[1] === "xlsx"
                                      ? icons.icon_chat_excel
                                      : chat.file?.advisor_student_file_name &&
                                        chat.file?.advisor_student_file_name?.match(
                                          /\.([a-z0-9]+)$/i
                                        )?.[1] === "pdf"
                                      ? icons.icon_chat_pdf
                                      : chat.file?.advisor_student_file_name &&
                                        chat.file?.advisor_student_file_name?.match(
                                          /\.([a-z0-9]+)$/i
                                        )?.[1] === "docx"
                                      ? icons.icon_chat_word
                                      : icons.document
                                  }
                                  style={{
                                    maxWidth: "100%",
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain", // Ensures image doesn't get distorted
                                  }}
                                  alt={
                                    chat.file?.advisor_student_file_name ??
                                    "file"
                                  }
                                />
                              </div>

                              {/* Column 2: File Name */}
                              <div>
                                <span>
                                  {chat.file?.advisor_student_file_name ??
                                    "No file name"}
                                </span>
                              </div>
                            </div>
                          )}
                        </Box>

                        {chat.sender !== "me" ? (
                          <img
                            src={icons.download_chat}
                            alt="download-icon"
                            style={{
                              width: "25px",
                              height: "25px",
                              marginLeft: "25px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              handleDownloadFile(
                                chat.file?.id_advisor_student_file,
                                chat.file?.advisor_student_file_name +
                                  "-" +
                                  new Date().getTime()
                              );
                            }}
                          />
                        ) : null}
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              ))}
        </Box>
      </Collapse>
    </Card>
  );
};

export default CardChat;
